import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { walletBalance, walletHistory, walletSummary, apiBaseUrl, createReferralCode, checkUserReferralCode } from '../../util/api';

// ================ Action types ================ //
export const WALLET_FETCH_REQUEST = 'app/WalletPage/WALLET_FETCH_REQUEST';
export const WALLET_FETCH_SUCCESS = 'app/WalletPage/WALLET_FETCH_SUCCESS';
export const WALLET_FETCH_ERROR = 'app/WalletPage/WALLET_FETCH_ERROR';
export const REFERRAL_CREATE_REQUEST = 'app/wallet/REFERRAL_CREATE_REQUEST';
export const REFERRAL_CREATE_SUCCESS = 'app/wallet/REFERRAL_CREATE_SUCCESS';
export const REFERRAL_CREATE_ERROR = 'app/wallet/REFERRAL_CREATE_ERROR';

// ================ Reducer ================ //
const initialState = {
  fetching: false,
  error: null,
  balance: 0,
  items: [],
  availableBalance: 0,
  totalCreditsEarned: 0,
  totalCreditsSpent: 0,
  totalWithdrawDebited: 0,
  totalHoldCredit: 0,
  totalHoldDebit: 0,
  referralCode: null,
  referralLoading: false,
  referralError: null,
};

export default function walletPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case WALLET_FETCH_REQUEST:
      return { ...state, fetching: true, error: null };
    case WALLET_FETCH_SUCCESS:
      return {
        ...state,
        fetching: false,
        error: null,
        balance: payload.balance || 0,
        items: Array.isArray(payload.items) ? payload.items : [],
        availableBalance: payload.availableBalance || 0,
        totalCreditsEarned: payload.totalCreditsEarned || 0,
        totalCreditsSpent: payload.totalCreditsSpent || 0,
        totalWithdrawDebited: payload.totalWithdrawDebited || 0,
        totalHoldCredit: payload.totalHoldCredit || 0,
        totalHoldDebit: payload.totalHoldDebit || 0,
      };
    case WALLET_FETCH_ERROR:
      return { ...state, fetching: false, error: payload };
    case REFERRAL_CREATE_REQUEST:
      return { ...state, referralLoading: true, referralError: null };
    case REFERRAL_CREATE_SUCCESS:
      return {
        ...state,
        referralLoading: false,
        referralCode: payload?.referralCode || payload?.referralCode || null,
      };
    case REFERRAL_CREATE_ERROR:
      return { ...state, referralLoading: false, referralError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //
export const walletFetchRequest = () => ({ type: WALLET_FETCH_REQUEST });
export const walletFetchSuccess = data => ({ type: WALLET_FETCH_SUCCESS, payload: data });
export const walletFetchError = e => ({ type: WALLET_FETCH_ERROR, error: true, payload: e });
export const referralCreateRequest = () => ({ type: REFERRAL_CREATE_REQUEST });
export const referralCreateSuccess = data => ({ type: REFERRAL_CREATE_SUCCESS, payload: data });
export const referralCreateError = error => ({ type: REFERRAL_CREATE_ERROR, payload: error, error: true });

// ================ Thunks ================ //
export const fetchWallet = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const userId = state?.user?.currentUser?.id?.uuid;
    if (!userId) return;
    dispatch(walletFetchRequest());
    const [bRes, hRes, sRes] = await Promise.all([
      walletBalance({ userId }),
      walletHistory({ userId, limit: 50, skip: 0 }),
      walletSummary({ userId }),
    ]);
    const balance =
      (bRes && bRes.data && (bRes.data.data?.balance ?? bRes.data.balance)) ?? 0;
    const items =
      (hRes && hRes.data && (hRes.data.data?.items ?? hRes.data.items)) ?? [];
    const summary = (sRes && sRes.data && (sRes.data.data ?? sRes.data)) || {};
    dispatch(walletFetchSuccess({
      balance,
      items,
      availableBalance: summary.availableBalance || 0,
      totalCreditsEarned: summary.totalCreditsEarned || 0,
      totalCreditsSpent: summary.totalCreditsSpent || 0,
      totalWithdrawDebited: summary.totalWithdrawDebited || 0,
      totalHoldCredit: summary.totalHoldCredit || 0,
      totalHoldDebit: summary.totalHoldDebit || 0,
    }));
    return summary.availableBalance;
  } catch (e) {
    const error = storableError(e);
    log.error(error, 'wallet-fetch-failed');
    dispatch(walletFetchError(error));
  }
};

export const createOrGetReferralForUser = (params) => async (dispatch, getState) => {
  try {
    const { currentUserId } = params;
    if (!currentUserId) {
      throw new Error('currentUserId is required');
    }
    const userId = currentUserId;
    if (!userId) return;
    dispatch(referralCreateRequest());

    const resp = await createReferralCode({ userId });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Referral request failed: ${resp.status} ${text}`);
    }

    const body = await resp.json();
    dispatch(referralCreateSuccess(body));
    return body;
  } catch (e) {
    const error = storableError(e);
    log.error(error, 'referral-create-failed');
    dispatch(referralCreateError(error));
    return null;
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  const currentUserId = currentUser?.id?.uuid;

  if (!currentUserId) {
    console.log('No current user ID available for WalletPage');
    return Promise.resolve();
  }

  return dispatch(fetchWallet()).catch(e => {
    console.error('Error loading AdminWithdrawalsPage data:', e);
    throw e;
  });
};